import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { FeatureGuard } from '@/components/feature-guard';
import { Page } from '@/components/page';
import { ScenePickerPanel } from '@/components/storyboard/ScenePickerPanel';
import { StoryboardEditor } from '@/components/storyboard/StoryboardEditor';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useStoryboard } from '@/hooks/useStoryboard';

function StoryboardContent() {
  const { novel } = useNovelRepository();
  const selectedNovelId = novel.id;
  const {
    selectedScene,
    setSelectedScene,
    storyboard,
    isGenerating,
    generateScript,
    updatePanel,
    batchUpdatePanels,
    regeneratePanel,
    generatePanelImage,
    refinePanelImage,
    acceptPanelImage,
    isGeneratingImage,
    isTokenDepleted,
  } = useStoryboard(selectedNovelId || null);
  const { t } = useTranslation();

  if (!novel.id) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {t('storyboard.select_novel')}
        </Typography>
      </Box>
    );
  }

  return (
    <Page
      title={t('storyboard.title')}
      description={t('storyboard.description')}
      layoutMode="workspace"
    >
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', gap: 2, width: '100%' }}>
        {/* Left Panel: Scene Selection */}
        <ScenePickerPanel
          selectedScene={selectedScene}
          onSelectScene={setSelectedScene}
          onGenerateScript={generateScript}
          isGenerating={isGenerating}
          hasStoryboard={!!storyboard}
          isTokenDepleted={isTokenDepleted}
        />

        {/* Main Area: Storyboard Editor */}
        <StoryboardEditor
          storyboard={storyboard}
          selectedScene={selectedScene}
          isGenerating={isGenerating}
          onUpdatePanel={updatePanel}
          onBatchUpdatePanels={batchUpdatePanels}
          onRegeneratePanel={regeneratePanel}
          onGeneratePanelImage={generatePanelImage}
          onRefinePanelImage={refinePanelImage}
          onAcceptPanelImage={acceptPanelImage}
          isGeneratingImage={isGeneratingImage}
          isTokenDepleted={isTokenDepleted}
        />
      </Box>
    </Page>
  );
}

export const Route = createFileRoute('/storyboard')({
  component: StoryboardPage,
});

export default function StoryboardPage() {
  return (
    <AuthGuard>
      <FeatureGuard featureKey="should_display_storyboard_page">
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <CircularProgress />
            </Box>
          }
        >
          <StoryboardContent />
        </Suspense>
      </FeatureGuard>
    </AuthGuard>
  );
}
